import {
  h,
  defineComponent,
  mergeProps,
  ref,
  onMounted,
  onBeforeUnmount,
  Comment,
  Fragment,
  Text,
  nextTick,
  watch,
  shallowReactive,
  provide,
  inject,
  useId
} from 'vue'
import { RouterLink } from 'vue-router'

export function slotContent(slot, fallback) {
  const nodes = slot?.()
  const meaningful = (nodes) =>
    nodes?.some(
      (node) =>
        node.type !== Comment &&
        (node.type === Fragment
          ? meaningful(node.children)
          : node.type !== Text || String(node.children).trim())
    )
  return meaningful(nodes) ? nodes : fallback
}
export const theme = (variant = 'secondary') => `btn-${variant}`
const linkProps = { to: [String, Object], href: String, disabled: Boolean }
export function link(p, attrs, children, cls = '') {
  if (p.disabled)
    attrs = {
      ...attrs,
      'aria-disabled': 'true',
      tabindex: -1,
      onClick: (e) => {
        e.preventDefault()
        e.stopImmediatePropagation()
      }
    }
  if (p.to && !p.disabled)
    return h(RouterLink, mergeProps(attrs, { to: p.to, class: cls }), { default: () => children })
  return h(
    p.href ? 'a' : 'button',
    mergeProps(attrs, {
      href: p.disabled ? undefined : p.href,
      type: p.href ? undefined : attrs.type || 'button',
      disabled: p.disabled,
      class: cls
    }),
    children
  )
}
export const BButton = defineComponent({
  inheritAttrs: false,
  props: {
    ...linkProps,
    variant: { default: 'secondary' },
    size: String,
    block: Boolean,
    pill: Boolean,
    squared: Boolean,
    type: { default: 'button' }
  },
  setup:
    (p, { attrs, slots }) =>
    () =>
      link(p, { ...attrs, type: p.type }, slots.default?.(), [
        'btn',
        theme(p.variant),
        p.size && `btn-${p.size}`,
        p.block && 'btn-block',
        p.pill && 'rounded-pill',
        p.squared && 'rounded-0'
      ])
})
export const BLink = defineComponent({
  inheritAttrs: false,
  props: linkProps,
  setup: (p, { attrs, slots }) => () => {
    if (p.to && !p.disabled) return link(p, attrs, slots.default?.())
    // BootstrapVue's destination-less BLink is an anchor action, whereas
    // BButton defaults to a native button. Keep these defaults separate.
    const href = p.href || (typeof p.to === 'string' && p.to) || '#'
    return h('a', {
      ...attrs,
      href,
      target: attrs.target || '_self',
      class: [attrs.class, p.disabled && 'disabled'],
      tabindex: p.disabled ? -1 : attrs.tabindex,
      'aria-disabled': p.disabled ? 'true' : undefined,
      onClick: (event) => {
        if (p.disabled) {
          event.preventDefault()
          event.stopImmediatePropagation()
          return
        }
        for (const handler of [attrs.onClick].flat()) {
          if (typeof handler === 'function') handler(event)
        }
        // Click-only links must not change the hash route or scroll to the top.
        if (href === '#') event.preventDefault()
      }
    }, slots.default?.())
  }
})
export function element(tag, cls, props = {}, extra = () => ({})) {
  return defineComponent({
    inheritAttrs: false,
    props,
    setup:
      (p, { attrs, slots }) =>
      () =>
        h(tag, mergeProps(attrs, { class: cls }, extra(p)), slots.default?.())
  })
}
export const BContainer = element('div', '', { fluid: [Boolean, String] }, (p) => ({
  class: p.fluid === true ? 'container-fluid' : p.fluid ? `container-${p.fluid}` : 'container'
}))
export const BRow = element(
  'div',
  'row',
  { noGutters: Boolean, alignH: String, alignV: String },
  (p) => ({
    class: [
      p.noGutters && 'no-gutters',
      p.alignH && `justify-content-${p.alignH}`,
      p.alignV && `align-items-${p.alignV}`
    ]
  })
)
export const BCol = element(
  'div',
  '',
  Object.fromEntries(
    [
      'cols',
      'sm',
      'md',
      'lg',
      'xl',
      'offset',
      'offsetSm',
      'offsetMd',
      'offsetLg',
      'order',
      'orderMd'
    ].map((k) => [k, [String, Number, Boolean]])
  ),
  (p) => ({
    class: [
      p.cols ? `col-${p.cols}` : !['sm', 'md', 'lg', 'xl'].some((k) => p[k]) && 'col',
      ...['sm', 'md', 'lg', 'xl']
        .filter((k) => p[k])
        .map((k) => (p[k] === true || p[k] === '' ? `col-${k}` : `col-${k}-${p[k]}`)),
      p.offset && `offset-${p.offset}`
    ]
  })
)
export const BCard = defineComponent({
  props: {
    noBody: Boolean,
    header: String,
    footer: String,
    title: String,
    subTitle: String,
    imgSrc: String,
    imgAlt: String,
    imgTop: Boolean,
    bgVariant: String,
    textVariant: String,
    borderVariant: String
  },
  setup:
    (p, { slots }) =>
    () =>
      h(
        'div',
        {
          class: [
            'card',
            p.bgVariant && `bg-${p.bgVariant}`,
            p.textVariant && `text-${p.textVariant}`,
            p.borderVariant && `border-${p.borderVariant}`
          ]
        },
        [
          p.imgSrc && h('img', { src: p.imgSrc, alt: p.imgAlt || '', class: 'card-img-top' }),
          (slots.header || p.header) &&
            h('div', { class: 'card-header' }, slots.header?.() || p.header),
          p.noBody
            ? slots.default?.()
            : h('div', { class: 'card-body' }, [
                p.title && h('h4', { class: 'card-title' }, p.title),
                p.subTitle && h('h6', { class: 'card-subtitle mb-2 text-muted' }, p.subTitle),
                slots.default?.()
              ]),
          (slots.footer || p.footer) &&
            h('div', { class: 'card-footer' }, slots.footer?.() || p.footer)
        ]
      )
})
export const BAlert = defineComponent({
  props: { show: [Boolean, Number, String], variant: { default: 'info' }, dismissible: Boolean },
  emits: ['dismissed'],
  setup(p, { slots, emit }) {
    const dismissed = ref(false)
    watch(
      () => p.show,
      () => {
        dismissed.value = false
      }
    )
    return () =>
      p.show && !dismissed.value
        ? h(
            'div',
            {
              class: ['alert', `alert-${p.variant}`, p.dismissible && 'alert-dismissible'],
              role: 'alert'
            },
            [
              slots.default?.(),
              p.dismissible &&
                h(
                  'button',
                  {
                    type: 'button',
                    class: 'close',
                    'aria-label': 'Close',
                    onClick: () => {
                      dismissed.value = true
                      emit('dismissed')
                    }
                  },
                  h('span', { 'aria-hidden': 'true' }, '×')
                )
            ]
          )
        : null
  }
})
export const BBadge = element(
  'span',
  'badge',
  { variant: { default: 'secondary' }, pill: Boolean },
  (p) => ({ class: [`badge-${p.variant}`, p.pill && 'badge-pill'] })
)
export const BSpinner = defineComponent({
  props: { small: Boolean, label: String, variant: String, type: String },
  setup: (p) => () =>
    h(
      'span',
      {
        role: 'status',
        class: [
          `spinner-${p.type || 'border'}`,
          p.small && `spinner-${p.type || 'border'}-sm`,
          p.variant && `text-${p.variant}`
        ]
      },
      h('span', { class: 'sr-only' }, p.label || 'Loading')
    )
})
export const BBreadcrumb = element('ol', 'breadcrumb')
export const BBreadcrumbItem = defineComponent({
  props: { ...linkProps, active: Boolean, text: String },
  setup:
    (p, { slots }) =>
    () =>
      h(
        'li',
        {
          class: ['breadcrumb-item', p.active && 'active'],
          'aria-current': p.active ? 'page' : undefined
        },
        p.active ? h('span', slots.default?.() || p.text) : link(p, {}, slots.default?.() || p.text)
      )
})
export const BNavbar = element(
  'nav',
  'navbar',
  { toggleable: [Boolean, String], variant: String, type: String, fixed: String },
  (p) => ({
    class: [
      p.toggleable && (p.toggleable === true ? 'navbar-expand' : `navbar-expand-${p.toggleable}`),
      p.variant && `bg-${p.variant}`,
      p.type && `navbar-${p.type}`,
      p.fixed && `fixed-${p.fixed}`
    ]
  })
)
export const BNavbarBrand = defineComponent({
  props: linkProps,
  setup:
    (p, { slots }) =>
    () =>
      link(p, {}, slots.default?.(), 'navbar-brand')
})
export const BNavbarNav = element('ul', 'navbar-nav')
export const BNavItem = defineComponent({
  props: { ...linkProps, active: Boolean },
  setup:
    (p, { slots }) =>
    () =>
      h('li', { class: 'nav-item' }, [
        link(p, {}, slots.default?.(), ['nav-link', p.active && 'active'])
      ])
})
const toggleTargets = shallowReactive(new Map())
export const BNavbarToggle = defineComponent({
  props: { target: String },
  setup: (p) => () =>
    h(
      'button',
      {
        type: 'button',
        class: 'navbar-toggler',
        'aria-label': 'Toggle navigation',
        'aria-controls': p.target,
        'aria-expanded': toggleTargets.get(p.target)?.open.value || false,
        onClick: () => toggleTargets.get(p.target)?.toggle()
      },
      h('span', { class: 'navbar-toggler-icon' })
    )
})
export const BCollapse = defineComponent({
  props: { id: String, visible: Boolean, modelValue: Boolean, isNav: Boolean },
  emits: ['update:modelValue', 'show', 'shown', 'hide', 'hidden'],
  setup(p, { slots, emit }) {
    const open = ref(p.visible || p.modelValue),
      root = ref(),
      transitioning = ref(false),
      height = ref('')
    let timer,
      sequence = 0
    const setOpen = async (value) => {
      if (value === open.value) return
      const token = ++sequence
      clearTimeout(timer)
      const before = root.value.getBoundingClientRect().height
      open.value = value
      transitioning.value = true
      height.value = value ? '0px' : `${before}px`
      emit(value ? 'show' : 'hide')
      emit('update:modelValue', value)
      await nextTick()
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (token !== sequence) return
          height.value = value ? `${root.value.scrollHeight}px` : '0px'
          const seconds = parseFloat(getComputedStyle(root.value).transitionDuration) || 0
          timer = setTimeout(() => {
            if (token !== sequence) return
            transitioning.value = false
            height.value = ''
            emit(value ? 'shown' : 'hidden')
          }, seconds * 1000)
        })
      )
    }
    watch(() => p.modelValue, setOpen)
    watch(() => p.visible, setOpen)
    onMounted(() => toggleTargets.set(p.id, { open, toggle: () => setOpen(!open.value) }))
    onBeforeUnmount(() => {
      sequence++
      clearTimeout(timer)
      toggleTargets.delete(p.id)
    })
    return () =>
      h(
        'div',
        {
          id: p.id,
          ref: root,
          class: [
            transitioning.value ? 'collapsing' : 'collapse',
            p.isNav && 'navbar-collapse',
            !transitioning.value && open.value && 'show'
          ],
          style: { height: height.value }
        },
        slots.default?.()
      )
  }
})
const dropdownContext = Symbol('dropdown')
const openDropdowns = new Set()
export const BDropdown = defineComponent({
  props: {
    id: String,
    text: String,
    right: Boolean,
    disabled: Boolean,
    split: Boolean,
    noCaret: Boolean,
    noFlip: Boolean,
    variant: { default: 'secondary' },
    size: String,
    nav: Boolean
  },
  emits: ['click', 'toggle', 'show', 'shown', 'hide', 'hidden'],
  setup(p, { slots, emit, expose }) {
    const open = ref(false),
      root = ref(),
      menu = ref(),
      toggle = ref(),
      position = ref({})
    const id = p.id || useId()
    let closeTimer, observer
    const updatePosition = () => {
      if (!open.value || p.nav || !menu.value) {
        position.value = {}
        return
      }
      const anchor = (p.split ? root.value : toggle.value).getBoundingClientRect()
      const bounds = root.value.getBoundingClientRect(),
        rect = menu.value.getBoundingClientRect()
      let left = p.right ? anchor.right - rect.width : anchor.left
      let top = anchor.bottom + 2
      let boundary = { left: 0, right: innerWidth, top: 0, bottom: innerHeight }
      for (
        let parent = root.value.parentElement;
        parent && parent !== document.body;
        parent = parent.parentElement
      ) {
        if (
          /(auto|scroll|hidden)/.test(
            getComputedStyle(parent).overflow +
              getComputedStyle(parent).overflowY +
              getComputedStyle(parent).overflowX
          )
        ) {
          const r = parent.getBoundingClientRect()
          boundary = {
            left: Math.max(boundary.left, r.left),
            right: Math.min(boundary.right, r.right),
            top: Math.max(boundary.top, r.top),
            bottom: Math.min(boundary.bottom, r.bottom)
          }
          break
        }
      }
      if (
        !p.noFlip &&
        top + rect.height > boundary.bottom &&
        anchor.top - rect.height >= boundary.top
      )
        top = anchor.top - rect.height
      left = Math.max(boundary.left + 5, Math.min(left, boundary.right - rect.width - 5))
      position.value = {
        left: `${left - bounds.left + root.value.scrollLeft}px`,
        top: `${top - bounds.top + root.value.scrollTop}px`,
        right: 'auto',
        margin: 0
      }
    }
    const setOpen = async (value, refocus = false) => {
      if (value === open.value || (value && p.disabled)) return
      const event = {
        type: value ? 'show' : 'hide',
        cancelable: true,
        defaultPrevented: false,
        componentId: id,
        target: root.value,
        preventDefault() {
          this.defaultPrevented = true
        }
      }
      emit(event.type, event)
      if (event.defaultPrevented) return
      clearTimeout(closeTimer)
      open.value = value
      if (value) {
        for (const close of openDropdowns) close()
        openDropdowns.add(closeSelf)
      } else openDropdowns.delete(closeSelf)
      await nextTick()
      if (value) {
        updatePosition()
        menu.value?.focus()
        observer?.observe(menu.value)
      } else {
        observer?.disconnect()
        position.value = {}
        if (refocus) toggle.value?.focus()
      }
      emit(value ? 'shown' : 'hidden')
    }
    const closeSelf = () => setOpen(false)
    provide(dropdownContext, { hide: () => setOpen(false, true) })
    expose({
      show: () => setOpen(true),
      hide: (refocus) => setOpen(false, refocus),
      toggle: () => setOpen(!open.value),
      updatePopper: updatePosition
    })
    const outside = (e) => {
      if (open.value && !root.value?.contains(e.target)) {
        clearTimeout(closeTimer)
        closeTimer = setTimeout(closeSelf, p.nav ? 300 : 0)
      }
    }
    const keyboard = async (e) => {
      if (e.key === 'Escape' && open.value) {
        e.preventDefault()
        e.stopPropagation()
        await setOpen(false, true)
        return
      }
      if (!['ArrowDown', 'ArrowUp'].includes(e.key) || e.target.closest('input,textarea,select'))
        return
      if (!open.value) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          await setOpen(true)
        }
        return
      }
      e.preventDefault()
      e.stopPropagation()
      const items = [
        ...menu.value.querySelectorAll('.dropdown-item:not(.disabled):not(:disabled)')
      ].filter((el) => el.getClientRects().length)
      const index = items.indexOf(document.activeElement)
      items[
        Math.max(0, Math.min(items.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)))
      ]?.focus()
    }
    onMounted(() => {
      document.addEventListener('click', outside)
      document.addEventListener('focusin', outside)
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
      observer = new ResizeObserver(updatePosition)
    })
    onBeforeUnmount(() => {
      clearTimeout(closeTimer)
      openDropdowns.delete(closeSelf)
      observer?.disconnect()
      document.removeEventListener('click', outside)
      document.removeEventListener('focusin', outside)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    })
    watch(
      () => p.disabled,
      (value) => {
        if (value) setOpen(false)
      }
    )
    return () =>
      h(
        p.nav ? 'li' : 'div',
        {
          id,
          ref: root,
          class: [
            p.nav ? 'nav-item b-nav-dropdown dropdown' : 'dropdown b-dropdown btn-group',
            open.value && 'show'
          ],
          onKeydown: keyboard
        },
        [
          p.split &&
            h(
              'button',
              {
                type: 'button',
                disabled: p.disabled,
                class: ['btn', theme(p.variant), p.size && `btn-${p.size}`],
                onClick: (e) => emit('click', e)
              },
              slotContent(slots['button-content'], p.text)
            ),
          h(
            'button',
            {
              type: 'button',
              id: `${id}__BV_toggle_`,
              ref: toggle,
              disabled: p.disabled,
              class: [
                p.nav ? 'nav-link' : 'btn',
                !p.nav && theme(p.variant),
                'dropdown-toggle',
                p.noCaret && 'dropdown-toggle-no-caret',
                p.split && 'dropdown-toggle-split',
                p.size && `btn-${p.size}`
              ],
              'aria-haspopup': 'menu',
              'aria-expanded': String(open.value),
              onMousedown: (e) => e.preventDefault(),
              onClick: (e) => {
                emit('toggle', e)
                setOpen(!open.value, open.value)
              }
            },
            p.split
              ? h('span', { class: 'sr-only' }, 'Toggle dropdown')
              : slotContent(slots['button-content'], p.text)
          ),
          h(
            'ul',
            {
              ref: menu,
              role: 'menu',
              tabindex: -1,
              'aria-labelledby': `${id}__BV_toggle_`,
              class: ['dropdown-menu', open.value && 'show', p.right && 'dropdown-menu-right'],
              style: position.value
            },
            slots.default?.()
          )
        ]
      )
  }
})
export const BNavItemDropdown = defineComponent({
  setup:
    (_, { attrs, slots }) =>
    () =>
      h(BDropdown, { ...attrs, nav: true }, slots)
})
export const BDropdownItem = defineComponent({
  inheritAttrs: false,
  props: linkProps,
  setup(p, { attrs, slots }) {
    const dropdown = inject(dropdownContext, null)
    return () =>
      h('li', { role: 'presentation' }, [
        link(
          { ...p, href: p.href || '#' },
          mergeProps(attrs, {
            role: 'menuitem',
            tabindex: p.disabled ? -1 : 0,
            onClick: (e) => {
              if (!p.disabled) {
                if (!p.to && !p.href) e.preventDefault()
                requestAnimationFrame(() => dropdown?.hide())
              }
            }
          }),
          slots.default?.(),
          ['dropdown-item', p.disabled && 'disabled']
        )
      ])
  }
})
export const BProgressBar = defineComponent({
  props: {
    value: Number,
    max: { default: 100 },
    variant: String,
    animated: Boolean,
    striped: Boolean,
    label: String
  },
  setup:
    (p, { slots }) =>
    () =>
      h(
        'div',
        {
          class: [
            'progress-bar',
            p.variant && `bg-${p.variant}`,
            (p.striped || p.animated) && 'progress-bar-striped',
            p.animated && 'progress-bar-animated'
          ],
          role: 'progressbar',
          'aria-valuenow': p.value,
          'aria-valuemin': 0,
          'aria-valuemax': p.max,
          style: { width: `${(100 * (p.value || 0)) / p.max}%` }
        },
        slots.default?.() || p.label
      )
})
export const BProgress = defineComponent({
  props: { value: Number, max: { default: 100 }, height: String },
  setup:
    (p, { slots }) =>
    () =>
      h(
        'div',
        { class: 'progress', style: { height: p.height } },
        slots.default?.() || h(BProgressBar, p)
      )
})
export const simpleComponents = {
  BCardHeader: element(
    'div',
    'card-header',
    { headerBgVariant: String, headerTextVariant: String },
    (p) => ({
      class: [
        p.headerBgVariant && `bg-${p.headerBgVariant}`,
        p.headerTextVariant && `text-${p.headerTextVariant}`
      ]
    })
  ),
  BCardBody: element('div', 'card-body'),
  BCardFooter: element('div', 'card-footer', { footerBgVariant: String }, (p) => ({
    class: p.footerBgVariant && `bg-${p.footerBgVariant}`
  })),
  BCardTitle: element('h4', 'card-title'),
  BCardSubTitle: element('h6', 'card-subtitle text-muted'),
  BCardText: element('p', 'card-text'),
  BCardImg: element('img', '', {
    top: Boolean, bottom: Boolean, start: Boolean, end: Boolean,
    left: Boolean, right: Boolean
  }, (p) => ({
    // Match BootstrapVue placement aliases and precedence. Consumed props must
    // not fall through as native attributes on the image.
    class: p.top ? 'card-img-top' : p.right || p.end ? 'card-img-right'
      : p.bottom ? 'card-img-bottom' : p.left || p.start ? 'card-img-left' : 'card-img'
  })),
  BButtonGroup: element('div', 'btn-group', { size: String, vertical: Boolean }, (p) => ({
    role: 'group',
    class: [p.size && `btn-group-${p.size}`, p.vertical && 'btn-group-vertical']
  })),
  BButtonToolbar: defineComponent({
    props: { keyNav: Boolean, justify: Boolean },
    setup(p, { slots }) {
      const root = ref()
      const items = () =>
        [
          ...root.value.querySelectorAll(
            'button:not(:disabled),a[href]:not(.disabled),input:not(:disabled),select:not(:disabled)'
          )
        ].filter((el) => el.getClientRects().length)
      onMounted(() => {
        if (p.keyNav)
          items().forEach((el) => {
            el.tabIndex = -1
          })
      })
      return () =>
        h(
          'div',
          {
            ref: root,
            role: 'toolbar',
            tabindex: p.keyNav ? 0 : undefined,
            class: ['btn-toolbar', p.justify && 'justify-content-between'],
            onFocusin: (e) => {
              if (p.keyNav && e.target === root.value && !root.value.contains(e.relatedTarget))
                items()[0]?.focus()
            },
            onKeydown: (e) => {
              if (!p.keyNav || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key))
                return
              e.preventDefault()
              const list = items(),
                index = list.indexOf(e.target),
                back = ['ArrowLeft', 'ArrowUp'].includes(e.key)
              if (index < 0 && !e.shiftKey) return
              list[
                e.shiftKey
                  ? back
                    ? 0
                    : list.length - 1
                  : Math.max(0, Math.min(list.length - 1, index + (back ? -1 : 1)))
              ]?.focus()
            }
          },
          slots.default?.()
        )
    }
  }),
  BListGroup: element('div', 'list-group', { flush: Boolean }, (p) => ({
    class: p.flush && 'list-group-flush'
  })),
  BListGroupItem: defineComponent({
    props: { ...linkProps, active: Boolean, variant: String },
    setup:
      (p, { slots }) =>
      () =>
        p.to || p.href
          ? link(p, {}, slots.default?.(), [
              'list-group-item',
              'list-group-item-action',
              p.active && 'active'
            ])
          : h(
              'div',
              {
                class: [
                  'list-group-item',
                  p.active && 'active',
                  p.variant && `list-group-item-${p.variant}`
                ]
              },
              slots.default?.()
            )
  }),
  BTableSimple: element('table', 'table b-table', { small: Boolean, bordered: Boolean }, (p) => ({
    class: [p.small && 'table-sm', p.bordered && 'table-bordered']
  })),
  BThead: element('thead', ''),
  BTbody: element('tbody', ''),
  BTr: element('tr', ''),
  BTh: element('th', ''),
  BTd: element('td', '')
}
